import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Chip,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { clientStageTasksApi, ClientStageTasks } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import TaskCompletionAdvanceDialog from './TaskCompletionAdvanceDialog'
import { workflowStageDefinitionsAtom } from '../../state/clients'
import { useAtom } from 'jotai'
import { userAtom } from '../../state/auth'

interface StageTasksChecklistProps {
  clientId: string
  stageId: string
  stageName: string
  onTasksChange?: (allRequiredComplete: boolean) => void
  clientName?: string
  workflowStage?: string
  onStageAdvanced?: () => void
}

export default function StageTasksChecklist({
  clientId,
  stageId,
  stageName,
  onTasksChange,
  clientName,
  workflowStage,
  onStageAdvanced,
}: StageTasksChecklistProps) {
  const { showSuccess, showError } = useNotification()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [stageTasks, setStageTasks] = useState<ClientStageTasks | null>(null)
  const [expanded, setExpanded] = useState(true)
  const [user] = useAtom(userAtom)
  const [showAdvanceModal, setShowAdvanceModal] = useState(false)
  const [workflowStages] = useAtom(workflowStageDefinitionsAtom)
  const stageLabels = useMemo(() =>
    Object.fromEntries(workflowStages.filter((s) => s.isActive).map((s) => [s.stageId, s.label])),
    [workflowStages]
  )

  // Active stage IDs in order, derived strictly from the DB-driven atom.
  // Drives next-stage computation so the FE never offers an inactive stage.
  const activeStageOrder = useMemo(
    () =>
      [...workflowStages]
        .filter((s) => s.isActive)
        .sort((a, b) => Number(a.sortOrder) - Number(b.sortOrder))
        .map((s) => s.stageId),
    [workflowStages]
  )

  // Define stages that auto-advance (don't show modal)
  const AUTO_ADVANCE_STAGES = ['north_star', 'deliverables_in_progress']

  // Check if current stage should show advance modal
  const shouldShowAdvanceModal = workflowStage && !AUTO_ADVANCE_STAGES.includes(workflowStage)

  // Get next stage for modal — sourced from the DB-driven active stage list
  const getNextStage = (currentStage: string): string | null => {
    const currentIndex = activeStageOrder.indexOf(currentStage)
    if (currentIndex === -1 || currentIndex === activeStageOrder.length - 1) return null
    return activeStageOrder[currentIndex + 1]
  }

  const nextStage = workflowStage ? getNextStage(workflowStage) : null
  const nextStageLabel = nextStage ? (stageLabels[nextStage] || nextStage) : ''

  // Only show modal to staff roles
  const isStaff = user?.role && ['admin', 'manager', 'client_success', 'resume_writer', 'coach'].includes(user.role)

  useEffect(() => {
    loadStageTasks()
  }, [clientId, stageId])

  const loadStageTasks = async () => {
    try {
      setLoading(true)
      const result = await clientStageTasksApi.get(clientId, stageId)
      // API returns single object when stageId is provided
      const tasks = Array.isArray(result) ? result[0] : result
      setStageTasks(tasks)
      if (onTasksChange && tasks) {
        onTasksChange(tasks.allRequiredComplete)
      }
    } catch (err) {
      console.error('Failed to load stage tasks:', err)
      // Don't show error - just means no tasks configured for this stage
      setStageTasks(null)
    } finally {
      setLoading(false)
    }
  }

  const handleToggleTask = async (
    gateTaskId: string,
    taskId: string | undefined,
    currentlyCompleted: boolean
  ) => {
    if (!stageTasks) return

    try {
      setSaving(gateTaskId)
      const updated = await clientStageTasksApi.completeTask(
        clientId,
        stageId,
        gateTaskId,
        !currentlyCompleted,
        undefined,
        taskId
      )
      setStageTasks(updated)
      if (onTasksChange) {
        onTasksChange(updated.allRequiredComplete)
      }

      // Show advance modal if:
      // 1. Task was just completed (not uncompleted)
      // 2. All required tasks are now complete
      // 3. Stage should show modal (not auto-advance)
      // 4. User is staff
      // 5. There is a next stage
      // 6. Client name is available
      if (
        !currentlyCompleted &&
        updated.allRequiredComplete &&
        shouldShowAdvanceModal &&
        isStaff &&
        nextStage &&
        clientName
      ) {
        setShowAdvanceModal(true)
      }

      showSuccess(currentlyCompleted ? 'Task unmarked' : 'Task completed')
    } catch (err) {
      console.error('Failed to update task:', err)
      showError('Failed to update task')
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return (
      <Box>
        <Skeleton variant="rounded" height={32} width="60%" sx={{ mb: 1.5 }} />
        <Skeleton variant="rounded" height={8} sx={{ mb: 2, borderRadius: 1 }} />
        <Skeleton variant="rounded" height={36} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={36} sx={{ mb: 1 }} />
        <Skeleton variant="rounded" height={36} />
      </Box>
    )
  }

  // No tasks configured for this stage
  if (!stageTasks || stageTasks.tasks.length === 0) {
    return null
  }

  const requiredTasks = stageTasks.tasks.filter((t) => t.required)
  const completedRequired = requiredTasks.filter((t) => t.completed).length
  const totalRequired = requiredTasks.length
  const progress = totalRequired > 0 ? (completedRequired / totalRequired) * 100 : 100

  return (
    <>
      <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: 'pointer',
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {stageName} Checklist
            </Typography>
            {stageTasks.allRequiredComplete ? (
              <Chip
                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                label="Ready to advance"
                size="small"
                color="success"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            ) : (
              <Chip
                icon={<WarningIcon sx={{ fontSize: 14 }} />}
                label={`${completedRequired}/${totalRequired} required`}
                size="small"
                color="warning"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}
          </Box>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>

        {totalRequired > 0 && (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              mt: 1,
              height: 4,
              borderRadius: 2,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                backgroundColor: stageTasks.allRequiredComplete ? 'success.main' : 'warning.main',
                borderRadius: 2,
              },
            }}
          />
        )}

        <Collapse in={expanded}>
          <List dense sx={{ mt: 1 }}>
            {stageTasks.tasks
              .sort((a, b) => {
                // Sort required first, then by completion status
                if (a.required !== b.required) return a.required ? -1 : 1
                return a.completed === b.completed ? 0 : a.completed ? 1 : -1
              })
              .map((task) => (
                <ListItem
                  key={task.gateTaskId}
                  sx={{
                    py: 0.5,
                    px: 0,
                    opacity: task.completed ? 0.7 : 1,
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    {saving === task.gateTaskId ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Checkbox
                        edge="start"
                        checked={task.completed}
                        onChange={() => handleToggleTask(task.gateTaskId, task.taskId, task.completed)}
                        size="small"
                        sx={{
                          color: task.required ? 'error.main' : 'grey.400',
                          '&.Mui-checked': {
                            color: 'success.main',
                          },
                        }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            textDecoration: task.completed ? 'line-through' : 'none',
                          }}
                        >
                          {task.title}
                        </Typography>
                        {task.required && (
                          <Typography
                            component="span"
                            sx={{ color: 'error.main', fontSize: '0.75rem' }}
                          >
                            *
                          </Typography>
                        )}
                      </Box>
                    }
                    secondary={
                      task.completed && task.completedBy ? (
                        <Tooltip title={task.completedAt || ''}>
                          <Typography variant="caption" color="text.secondary">
                            Completed by {task.completedBy}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Box component="span" sx={{ display: 'flex', flexDirection: 'column' }}>
                          {task.assignedTo && (
                            <Tooltip title={task.assignedTo}>
                              <Typography variant="caption" color="text.secondary" component="span">
                                Assignee:{' '}
                                <Typography variant="caption" component="span" color="primary.main" sx={{ fontWeight: 500 }}>
                                  {task.assignedTo.split('@')[0].replace(/[._]/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                                </Typography>
                              </Typography>
                            </Tooltip>
                          )}
                          {task.description && (
                            <Typography variant="caption" color="text.secondary" component="span">
                              {task.description}
                            </Typography>
                          )}
                        </Box>
                      )
                    }
                  />
                </ListItem>
              ))}
          </List>

          {stageTasks.overrideUsed && (
            <Box
              sx={{
                mt: 1,
                p: 1,
                backgroundColor: 'warning.light',
                opacity: 0.2,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'warning.main',
              }}
            >
              <Typography variant="caption" color="warning.main">
                Override used by {stageTasks.overrideBy} on{' '}
                {stageTasks.overrideAt && new Date(stageTasks.overrideAt).toLocaleDateString()}
                {stageTasks.overrideReason && `: "${stageTasks.overrideReason}"`}
              </Typography>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>

    {/* Task Completion Advance Modal */}
    {clientName && workflowStage && nextStage && (
      <TaskCompletionAdvanceDialog
        open={showAdvanceModal}
        onClose={() => setShowAdvanceModal(false)}
        clientId={clientId}
        clientName={clientName}
        currentStageLabel={stageName}
        nextStageLabel={nextStageLabel}
        onAdvanced={() => {
          setShowAdvanceModal(false)
          if (onStageAdvanced) {
            onStageAdvanced()
          }
        }}
      />
    )}
  </>
  )
}
